import { type ContextType, useCallback, useEffect, useRef } from 'react';

import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { ReactEditorViewContext } from '@atlaskit/editor-common/ui-react';

import { findWhiteboardNodePosByContentId } from '../../editor-commands/replace-whiteboard-embed';
import { setRemixDiagramContentId } from '../../editor-commands/set-remix-diagram-content-id';
import type { MalleableUiPlugin } from '../../malleableUiPluginType';

// Sourced from the editor-view context type so this file doesn't import the prosemirror EditorView
// re-export directly.
type EditorView = NonNullable<ContextType<typeof ReactEditorViewContext>['editorView']>;

/**
 * Owns the diagram-specific ready-signal logic (board-node tracking + scroll target). Called
 * unconditionally; for non-diagram remixes it returns inert values so callers use the shared path.
 */
export const useRemixDiagramReadySignal = ({
	api,
	isThinking,
}: {
	api?: ExtractInjectionAPI<MalleableUiPlugin>;
	isThinking: boolean;
}): {
	recordDiagramContentId: (contentId: string) => void;
	remixDiagramContentId: string | undefined;
	resolveDiagramScrollTarget: (editorView: EditorView) => HTMLElement | null;
} => {
	// contentId of the diagram produced by the in-flight remix (set at streaming-start).
	// Presence marks this remix as a diagram and lets the ready-signal UI target the board node.
	const remixDiagramContentId = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.remixDiagramContentId,
	);

	// Clear a diagram contentId left over from a previous remix when a new one starts (rising edge
	// of isThinking), so a later non-diagram remix never targets a stale board.
	const prevIsThinkingRef = useRef(false);
	useEffect(() => {
		const justStartedThinking = isThinking && !prevIsThinkingRef.current;
		prevIsThinkingRef.current = isThinking;
		if (justStartedThinking && remixDiagramContentId !== undefined) {
			api?.core.actions.execute(setRemixDiagramContentId(undefined));
		}
	}, [isThinking, remixDiagramContentId, api]);

	// Record the streaming diagram's contentId (called from the 'suppressed' finalize-status branch
	// as the board starts streaming in).
	const recordDiagramContentId = useCallback(
		(contentId: string) => {
			api?.core.actions.execute(setRemixDiagramContentId(contentId));
		},
		[api],
	);

	// The whiteboard's DOM element (by contentId) so the scroll can target the board. Null for
	// non-diagram remixes or before the board mounts — callers fall back to the anchor resolution.
	const resolveDiagramScrollTarget = useCallback(
		(editorView: EditorView): HTMLElement | null => {
			if (remixDiagramContentId === undefined) {
				return null;
			}
			const boardPos = findWhiteboardNodePosByContentId(
				editorView.state.doc,
				remixDiagramContentId,
			);
			const boardDom = boardPos !== undefined ? editorView.nodeDOM(boardPos) : null;
			return boardDom instanceof HTMLElement ? boardDom : (boardDom?.parentElement ?? null);
		},
		[remixDiagramContentId],
	);

	return { recordDiagramContentId, remixDiagramContentId, resolveDiagramScrollTarget };
};
